import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Package, Plus, Trash, CheckCircle, WarningCircle, XCircle, ShoppingCart, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi, PART_STATUS } from './devApi';
import { CitationList } from './Citations';
import { AddPartDialog } from './AddPartDialog';
import { MaterialLines, isResolved, MATERIAL_SOURCE_LABELS } from './MaterialLines';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PartsRequestDialog } from './PartsRequestDialog';
import { PartsAiSuggest } from './PartsAiSuggest';
import { EngAssetFormDialog } from '../EngAssetFormDialog';
import { engApi } from '../engApi';
import { groupFromSku } from '@/lib/skuGroup';

const TH = 'whitespace-nowrap px-2 py-1.5 text-xs font-semibold text-zinc-600';
const TD = 'whitespace-nowrap px-2 py-2 text-sm tabular-nums text-zinc-900';

const BuildQty = ({ value, canEdit, onSave }) => {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-zinc-300 bg-white p-3 shadow-sm"
      data-testid="dev-build-qty">
      <div>
        <p className="text-xs font-semibold text-zinc-600">製作台数（必須）</p>
        <div className="mt-1 flex items-center gap-2">
          <Input type="number" min={1} value={v} onChange={(e) => setV(e.target.value)}
            className="w-24" disabled={!canEdit} data-testid="dev-build-qty-input" />
          <span className="text-sm text-zinc-700">台</span>
          <Button size="sm" variant="outline" disabled={!canEdit || !(Number(v) > 0)}
            onClick={() => onSave(Number(v))} data-testid="dev-build-qty-save">保存</Button>
        </div>
      </div>
      <p className="pb-1 text-xs text-zinc-600">
        必要数 = 1台あたりの必要数 × 製作台数。計算は隠しません。
      </p>
    </div>
  );
};

const Readiness = ({ readiness, summary }) => {
  const ok = readiness?.can_build;
  return (
    <div className={`rounded-lg border px-4 py-3 ${ok ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
      data-testid="dev-parts-readiness">
      <p className={`flex items-center gap-1.5 text-sm font-bold ${ok ? 'text-emerald-900' : 'text-amber-900'}`}>
        {ok ? <CheckCircle size={16} weight="fill" /> : <WarningCircle size={16} weight="fill" />}
        {ok ? '必要部品はすべて揃っています（試作に投入できます）' : '部品がそろうまで試作に投入できません'}
      </p>
      {!ok && (readiness?.blockers || []).length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-amber-900">
          {readiness.blockers.map((b, i) => <li key={i} data-testid={`dev-parts-blocker-${i}`}>{b}</li>)}
        </ul>
      )}
      {(readiness?.notes || []).length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-zinc-700" data-testid="dev-parts-notes">
          {readiness.notes.map((n, i) => <li key={i}>参考: {n}（試作の妨げにはなりません）</li>)}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap gap-3 text-xs font-semibold text-zinc-700">
        <span data-testid="dev-parts-count-lines">部品 {summary.lines}件</span>
        <span className="text-emerald-800" data-testid="dev-parts-count-available">
          在庫あり {summary.available}
        </span>
        <span className="text-amber-800" data-testid="dev-parts-count-procurement">
          手配が必要 {summary.need_procurement}
        </span>
        <span className="text-rose-800" data-testid="dev-parts-count-missing">
          情報不足 {summary.missing_info}
        </span>
        <span data-testid="dev-parts-count-requested">依頼済み {summary.requested_lines}</span>
        <span data-testid="dev-parts-total-need">不足合計 {summary.total_need_qty}</span>
      </div>
    </div>
  );
};

const PartRow = ({ l, checked, onCheck, canEdit, onQty, onDelete, onRequest, onPromote, onEditMaterials, onMakeType, onImportMaterials }) => {
  const st = PART_STATUS[l.status] || PART_STATUS.available;
  const [qty, setQty] = useState(l.qty_per_unit);
  useEffect(() => { setQty(l.qty_per_unit); }, [l.qty_per_unit]);
  return (
    <>
      <tr className="group hover:bg-zinc-50" data-testid={`dev-part-row-${l.id}`}>
        <td className="sticky left-0 z-10 bg-white px-2 py-2 group-hover:bg-zinc-50">
          <Checkbox checked={checked} disabled={l.need <= 0} onCheckedChange={(v) => onCheck(!!v)}
            data-testid={`dev-part-select-${l.id}`} />
        </td>
        <td className="px-2 py-2">
          <StatusBadge tone={st.tone} testid={`dev-part-status-${l.id}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {l.status_label || st.label}
          </StatusBadge>
          {(l.missing_reasons || []).length > 0 && (
            <p className="mt-0.5 max-w-[8rem] text-xs text-rose-700" data-testid={`dev-part-missing-${l.id}`}>
              {l.missing_reasons.join('・')}
            </p>
          )}
        </td>
        <td className="w-[19rem] max-w-[19rem] px-2 py-2" data-testid={`dev-part-name-cell-${l.id}`}>
          <p className="truncate text-sm font-semibold text-zinc-900" title={l.name}>{l.name}</p>
          <p className="text-xs text-zinc-600 truncate"
            title={[l.model_no, l.maker, l.maker_pn, l.part_no].filter(Boolean).join(' · ')}>
            {[l.model_no, l.maker, l.maker_pn, l.part_no].filter(Boolean).join(' · ') || '—'}
          </p>
          <p className="mt-0.5 flex items-center gap-1 overflow-hidden whitespace-nowrap text-[11px]"
            data-testid={`dev-part-badges-${l.id}`}>
            <StatusBadge tone={l.source === 'unregistered' ? 'neutral' : 'info'}
              testid={`dev-part-source-${l.id}`}>{l.source_label}</StatusBadge>
            {/* 購入品 / 自社製作。既存のバッジ表現をそのまま使う（新しい見た目は作らない）。
                手配区分は行で直せる（部品依頼 / 製作依頼 の振り分けがこれで決まる）。 */}
            {canEdit ? (
              <select value={l.make_type || ''} onChange={(e) => onMakeType(e.target.value)}
                data-testid={`dev-part-make-${l.id}`}
                title="手配区分 — 購入品なら部品依頼、自社製作なら製作依頼になります"
                className="rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700">
                <option value="">手配区分 未設定</option>
                <option value="purchased">購入品</option>
                <option value="in_house">自社製作</option>
              </select>
            ) : l.make_type_label && (
              <StatusBadge tone="neutral" testid={`dev-part-make-${l.id}`}>{l.make_type_label}</StatusBadge>
            )}
            {l.flow_status_label && (
              <StatusBadge tone={l.flow_status === 'used' ? 'success'
                : l.flow_status === 'arrived' ? 'info'
                  : l.flow_status === 'ordering' ? 'warning' : 'neutral'}
                testid={`dev-part-flow-${l.id}`}>{l.flow_status_label}</StatusBadge>
            )}
            {l.ai_suggested && (
              <StatusBadge tone="neutral" testid={`dev-part-ai-${l.id}`}>AI提案</StatusBadge>
            )}
            {/* R3 fix · 過去に行で上書きされた値。データはそのまま残す。 */}
            {l.line_differs_from_master && (
              <StatusBadge tone="warning" testid={`dev-part-override-${l.id}`}>
                行の値（マスターと異なる）
              </StatusBadge>
            )}
            {(l.requests || []).map((r) => (
              <StatusBadge key={r.request_id}
                tone={r.status === 'cancelled' ? 'danger' : r.counts_as_reserved ? 'info' : 'neutral'}
                testid={`dev-part-req-${l.id}-${r.request_id}`}>
                依頼 {r.request_no} · {r.qty}{l.uom} · {r.status_label}
              </StatusBadge>
            ))}
          </p>
        </td>
        <td className={`${TD} w-[6.5rem] max-w-[6.5rem] overflow-hidden`}
          data-testid={`dev-part-drawing-${l.id}`}>
          <span className="block truncate" title={l.drawing_no || '図番なし'}>
            {l.drawing_no || '—'}
            {l.drawing_revision ? <span className="ml-1 font-semibold">{l.drawing_revision}</span> : null}
          </span>
          {(l.revision_history || []).length > 0 && (
            <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-zinc-500"
              data-testid={`dev-part-rev-history-${l.id}`}>
              <ClockCounterClockwise size={11} /> 旧版{l.revision_history.length}
            </span>
          )}
        </td>
        <td className={TD}>
          {canEdit ? (
            <Input type="number" min={0.0001} value={qty} className="h-7 w-20 text-sm"
              onChange={(e) => setQty(e.target.value)}
              onBlur={() => Number(qty) > 0 && Number(qty) !== l.qty_per_unit && onQty(Number(qty))}
              data-testid={`dev-part-qty-${l.id}`} />
          ) : `${l.qty_per_unit}`}
        </td>
        <td className={`${TD} font-bold`} data-testid={`dev-part-required-${l.id}`}>
          {l.required_total}{l.uom}
          {l.installed > 0 && (
            <span className="ml-1 block text-[10px] font-semibold text-teal-700"
              data-testid={`dev-part-installed-${l.id}`}>
              試作に搭載 {l.installed}
            </span>
          )}
        </td>
        <td className={TD} data-testid={`dev-part-available-${l.id}`}>
          {l.available_known ? `${l.available}` : '—'}
        </td>
        <td className={TD}>{l.reserved}</td>
        <td className={`${TD} font-bold ${l.need > 0 ? 'text-amber-800' : 'text-zinc-500'}`}
          data-testid={`dev-part-need-${l.id}`}>{l.need}</td>
        <td className={TD}>{l.pack_size ? `${l.pack_size}${l.pack_uom ? `/${l.pack_uom}` : ''}` : '—'}</td>
        <td className={TD} data-testid={`dev-part-order-${l.id}`}>
          {l.need > 0 ? (
            <>
              {l.recommended_order_qty}
              {l.recommended_packs ? (
                <span className="ml-1 text-xs text-zinc-600">（{l.recommended_packs}パック）</span>
              ) : null}
            </>
          ) : '—'}
        </td>
        <td className={TD}>{l.need > 0 ? l.remaining_after_order : '—'}</td>
        <td className="sticky right-0 z-10 whitespace-nowrap border-l border-zinc-200 bg-white px-2 py-2 text-right">
          {l.need > 0 && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!canEdit}
              onClick={onRequest} data-testid={`dev-part-request-btn-${l.id}`}>
              <ShoppingCart size={12} className="mr-1" />
              {l.action === 'fabricate' ? '製作依頼' : '部品依頼'}
            </Button>
          )}
          {l.source === 'unregistered' && (
            l.promoted_asset_id ? (
              <Link to={`/engineering/assets?asset=${l.promoted_asset_id}`}
                className="ml-1 inline-flex h-7 items-center gap-1 rounded-md border border-teal-300 bg-teal-50 px-2 text-xs font-semibold text-teal-900"
                data-testid={`dev-part-promoted-badge-${l.id}`}
                title={`技術資産 ${l.promoted_asset_no} として登録済み${l.promoted_by_name ? `（${l.promoted_by_name}）` : ''}`}>
                <CheckCircle size={12} /> 資産 {l.promoted_asset_no}
              </Link>
            ) : (
              <Button size="sm" variant="outline" className="ml-1 h-7 px-2 text-xs" disabled={!canEdit}
                onClick={onPromote} data-testid={`dev-part-promote-btn-${l.id}`}
                title="この部品を技術資産として登録します（品番発番・現在地・受注分類が必要です）">
                <Package size={12} className="mr-1" /> 技術資産として登録
              </Button>
            )
          )}
          <Button size="sm" variant="ghost" className="ml-1 h-7 px-2 text-xs text-rose-700"
            disabled={!canEdit} onClick={onDelete} data-testid={`dev-part-delete-${l.id}`}>
            <Trash size={12} />
          </Button>
        </td>
      </tr>
      {((l.material_lines || []).length > 0 || l.make_type === 'in_house') && (
        <tr className="bg-sky-50/40">
          <td />
          <td colSpan={12} className="px-2 pb-2">
            {/* 使用材料は読み取り専用で見せる。編集口はここに置かない —
                昇格後は構成の持ち主が下流（製造の部品表）に移るため、
                同じ構成が2箇所で編集できる状態を作らない。 */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
              data-testid={`dev-part-materials-${l.id}`}>
              <span className="font-semibold text-sky-800">使用材料</span>
              {l.material_lines.map((m, i) => (
                <span key={i} className="rounded bg-white px-2 py-0.5 text-zinc-700 ring-1 ring-sky-100"
                  data-testid={`dev-part-material-view-${l.id}-${i}`}>
                  {m.component_sku ? `${m.component_sku} ` : ''}{m.component_name}
                  {/* 出所（製造／技術資産）は常に出す。同じ品名が両方の台帳に
                      あり得るので、どちらの在庫を食う行なのかを隠さない。 */}
                  <span className="ml-1.5 text-zinc-500"
                    data-testid={`dev-part-material-view-source-${l.id}-${i}`}>
                    {MATERIAL_SOURCE_LABELS[m.component_source] || m.component_source}
                  </span>
                  <span className="ml-1.5 font-medium tabular-nums">
                    {m.qty}{m.uom}{m.qty_basis === 'per_unit' ? '／台' : '（合計）'}
                  </span>
                  {m.notes && <span className="ml-1.5 text-zinc-500">{m.notes}</span>}
                </span>
              ))}
              {l.make_type === 'in_house' && !l.material_lines_locked && (
                <button type="button"
                  onClick={() => onEditMaterials(l)}
                  data-testid={`dev-part-material-edit-${l.id}`}
                  className="text-[10px] font-semibold text-sky-700 underline hover:text-sky-900">
                  {(l.material_lines || []).length ? '材料を編集' : '材料を追加'}
                </button>
              )}
              {l.make_type === 'in_house' && !l.material_lines_locked && l.item_id && (
                <button type="button" onClick={() => onImportMaterials(l)}
                  data-testid={`dev-part-material-import-${l.id}`}
                  className="text-[10px] font-semibold text-sky-700 underline hover:text-sky-900">
                  部品マスタから材料を取込
                </button>
              )}
              {l.material_lines_locked && (
                <span className="text-[10px] text-zinc-500"
                  data-testid={`dev-part-materials-locked-${l.id}`}>
                  技術資産として登録済み — 構成の管理は下流に移りました（ここでは変更できません）
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
      {(l.citations || []).length > 0 && (
        <tr className="bg-zinc-50/60">
          <td />
          <td colSpan={12} className="px-2 pb-2">
            <CitationList citations={l.citations} testid={`dev-part-citations-${l.id}`} />
          </td>
        </tr>
      )}
    </>
  );
};

/** 必要部品計画（フェーズ2） — 必要数・在庫・不足・推奨発注をすべて表示し、部品依頼を作る。 */
export const PartsPlanning = ({ devId, canEdit, linkedDocs, onChanged, onParts }) => {
  // 昇格プレフィル用。品番グループの導出規則は 必要部品を追加 と同一（lib/skuGroup）。
  const [skuGroups, setSkuGroups] = useState([]);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [reqLines, setReqLines] = useState(null);
  const [promote, setPromote] = useState(null);
  // 使用材料の編集（昇格前のみ）。作成後に直せないと打ち間違いが直せなくなる。
  const [matEdit, setMatEdit] = useState(null);
  const [matLines, setMatLines] = useState([]);
  const [matSaving, setMatSaving] = useState(false);

  const load = useCallback(() => {
    devApi.parts(devId).then((d) => { setData(d); onParts?.(d.lines || []); })
      .catch(() => toast.error('部品計画を読み込めませんでした'));
  }, [devId, onParts]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!promote || skuGroups.length) return;
    engApi.skuGroups().then((g) => setSkuGroups(g || [])).catch(() => setSkuGroups([]));
  }, [promote, skuGroups.length]);

  const refresh = (view) => {
    if (view) { setData(view); onParts?.(view.lines || []); } else load();
    onChanged?.();
  };

  const saveQty = async (qty) => {
    try { refresh(await devApi.setBuildQty(devId, qty)); toast.success('製作台数を保存しました'); }
    catch (e) { toast.error(e.response?.data?.detail || '保存できませんでした'); }
  };
  const patchQty = async (pid, qty) => {
    try { refresh(await devApi.patchPart(devId, pid, { qty_per_unit: qty })); }
    catch (e) { toast.error(e.response?.data?.detail || '更新できませんでした'); }
  };
  const importMaterials = async (pid) => {
    try {
      refresh(await devApi.importPartMaterials(devId, pid));
      toast.success('部品マスタの使用材料を取り込みました');
    } catch (e) { toast.error(e.response?.data?.detail || '取り込めませんでした'); }
  };

  const setMakeType = async (pid, v) => {
    try {
      refresh(await devApi.patchPart(devId, pid, { make_type: v || null }));
      toast.success(v === 'in_house' ? '自社製作にしました（製作依頼）' : '購入品にしました（部品依頼）');
    } catch (e) { toast.error(e.response?.data?.detail || '更新できませんでした'); }
  };
  const remove = async (pid) => {
    try { refresh(await devApi.deletePart(devId, pid)); toast.success('部品を削除しました'); }
    catch (e) { toast.error(e.response?.data?.detail || '削除できませんでした'); }
  };

  if (!data) {
    return <p className="text-sm text-zinc-600" data-testid="dev-parts-loading">部品計画を読み込み中…</p>;
  }
  const lines = data.lines || [];
  const selected = lines.filter((l) => sel[l.id] && l.need > 0);
  const shortages = lines.filter((l) => l.need > 0);

  return (
    <section className="space-y-3" data-testid="dev-parts-planning">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-lg font-bold text-zinc-900">
          <Package size={18} weight="duotone" className="text-zinc-500" /> 必要部品と調達
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={!canEdit || !data.build_quantity}
            onClick={() => setAddOpen(true)} data-testid="dev-parts-add-btn">
            <Plus size={14} className="mr-1" /> 部品を追加
          </Button>
          {/* R4 · ひとつの「部品を手配」— 購入品は REQ・自社製作は 製作依頼・在庫は 出庫 */}
          <Button size="sm" disabled={!canEdit || (data.lines || []).length === 0}
            onClick={() => setReqLines(selected.length > 0 ? selected
              : (shortages.length > 0 ? shortages : (data.lines || [])))}
            data-testid="dev-parts-request-batch-btn">
            <ShoppingCart size={14} className="mr-1" />
            部品を手配{selected.length > 0 ? `（${selected.length}件）` : ''}
          </Button>
        </div>
      </div>

      <BuildQty value={data.build_quantity} canEdit={canEdit} onSave={saveQty} />
      <Readiness readiness={data.readiness} summary={data.summary} />

      {lines.length === 0 ? (
        <EmptyState icon={XCircle} title="必要部品はまだ登録されていません"
          description="AIアシスタントに提案してもらうか、手入力で追加してください。"
          testid="dev-parts-empty" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-300 bg-white shadow-sm">
          <table className="w-full border-collapse">
            <thead className="border-b border-zinc-300 bg-zinc-100">
              <tr className="text-left">
                <th className={`${TH} sticky left-0 z-20 bg-zinc-100`} />
                <th className={TH}>状態</th>
                {/* 内容の列。他は nowrap の数値列なので、ここが残り幅を取る（DataTable と同じ規則）。 */}
                <th className={`${TH} w-[19rem]`}>部品</th>
                <th className={`${TH} w-[6.5rem]`}>図番 / 版</th>
                <th className={TH}>1台あたり</th>
                <th className={TH}>必要数</th>
                <th className={TH}>在庫</th>
                <th className={TH}>依頼済み</th>
                <th className={TH}>不足</th>
                <th className={TH}>入数</th>
                <th className={TH}>推奨発注</th>
                <th className={TH}>残る見込み</th>
                <th className={`${TH} sticky right-0 z-10 border-l border-zinc-200 bg-zinc-100 text-right`}>操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {lines.map((l) => (
                <PartRow key={l.id} l={l} canEdit={canEdit} checked={!!sel[l.id]}
                  onEditMaterials={(row) => { setMatLines((row.material_lines || []).map((m) => ({ ...m, qty: String(m.qty) }))); setMatEdit(row); }}
                  onCheck={(v) => setSel({ ...sel, [l.id]: v })}
                  onQty={(q) => patchQty(l.id, q)} onDelete={() => remove(l.id)}
                  onMakeType={(v) => setMakeType(l.id, v)}
                  onImportMaterials={() => importMaterials(l.id)}
                  onRequest={() => setReqLines([l])}
                  onPromote={() => setPromote(l)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PartsAiSuggest devId={devId} linkedDocs={linkedDocs} canEdit={canEdit}
        buildQty={data.build_quantity} onAdded={() => refresh()} />

      {/* 使用材料の編集（昇格前のみ）。昇格後は API も 409 で止める。 */}
      <Dialog open={!!matEdit} onOpenChange={(o) => !o && setMatEdit(null)}>
        <DialogContent className="max-w-2xl" data-testid="dev-part-material-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              使用材料 — {matEdit?.name}
            </DialogTitle>
          </DialogHeader>
          <MaterialLines lines={matLines} onChange={setMatLines} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatEdit(null)}
              data-testid="dev-part-material-edit-cancel">キャンセル</Button>
            <Button disabled={matSaving} data-testid="dev-part-material-edit-save"
              onClick={async () => {
                setMatSaving(true);
                try {
                  await devApi.patchPart(devId, matEdit.id, {
                    material_lines: matLines.filter(isResolved).map((l) => ({
                      component_item_id: l.component_item_id,
                      component_source: l.component_source,
                      qty: Number(l.qty), qty_basis: l.qty_basis, uom: l.uom,
                      notes: (l.notes || '').trim() || null,
                    })),
                  });
                  toast.success('使用材料を更新しました');
                  setMatEdit(null);
                  refresh();
                } catch (e) {
                  const d = e?.response?.data;
                  toast.error(d?.detail || '使用材料の更新に失敗しました');
                } finally { setMatSaving(false); }
              }}>
              {matSaving ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddPartDialog devId={devId} open={addOpen} onOpenChange={setAddOpen}
        onAdded={() => refresh()} />
      <PartsRequestDialog devId={devId} open={!!reqLines} lines={reqLines || []}
        onOpenChange={(o) => !o && setReqLines(null)}
        onCreated={(r) => { setSel({}); refresh(r.view); }} />
      {/* 未登録部品 → 技術資産への昇格。人が明示的に押したときだけ動く。
          受入起点の auto_register_from_receive とは別経路（あちらは一切変えていない）。 */}
      <EngAssetFormDialog open={!!promote} onClose={() => setPromote(null)}
        prefill={promote ? {
          name: promote.name || '',
          model_no: promote.model_no || '',
          drawing_no: promote.drawing_no || '',
          maker: promote.maker || '',
          maker_pn: promote.maker_pn || '',
          material: promote.material || '',
          uom: promote.uom || '個',
          // 数量は「いま在るもの」だけを引き継ぐ。在庫が未記録なら 0。
          // required_total / qty_per_unit にフォールバックしてはいけない —
          // 「必要な数」を「在庫として持っている数」として在庫台帳に書いてしまう。
          qty_total: promote.stock_qty ?? 0,
          unit_price: promote.unit_cost ?? promote.unit_price ?? '',
          reference_url: promote.reference_url || '',
          notes: promote.notes || '',
          // 計画時点で入れた項目は昇格時に再入力させない。
          // 品番グループは紐付け先の品番から導出できるものは行に保存していないので、
          // master_sku から同じ導出規則で補う（控えの二重管理を避ける）。
          sku_group_id: promote.sku_group_id || groupFromSku(promote.master_sku, skuGroups) || '',
          category_id: promote.category_id || '',
          category_name: promote.category_name || '',
          supplier_id: promote.supplier_id || '',
          supplier_name: promote.supplier_name || '',
          photo_url: promote.photo_url || '',
          photo_filename: promote.photo_filename || '',
          photo_is_pdf: !!promote.photo_is_pdf,
        } : null}
        sourceRef={promote ? { development_id: devId, part_id: promote.id } : null}
        onSaved={() => { toast.success('技術資産として登録しました'); setPromote(null); refresh(); }} />
    </section>
  );
};
